import { useEffect, useState } from 'react';
import type { RoleSummary } from '@todo/shared';
import { roleApi } from '../api/role.api';

/**
 * Pick what a person is ALLOWED TO DO.
 *
 * Not a department (where they work) and not a job title (what they are
 * called) - those are separate fields, because two site engineers can hold
 * different roles and an Admin can sit in any department.
 *
 * Leaving it unset is meaningful: the API resolves the organisation's default
 * role in SQL, so the value is never hardcoded in either app.
 */
interface RoleSelectProps {
  value: string | undefined;
  onChange: (roleId: string | undefined) => void;
  error?: string;
  disabled?: boolean;
  /**
   * Built-in roles to leave off the list entirely - e.g. an Admin adding or
   * editing a person is never offered Super Admin, so they cannot hand out
   * a role that outranks their own. Mirrors the server-side check in
   * user.service.ts; this is the convenience copy, not the control.
   */
  hiddenSystemKeys?: string[];
}

export function RoleSelect({
  value,
  onChange,
  error,
  disabled,
  hiddenSystemKeys = [],
}: RoleSelectProps) {
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    roleApi
      .list()
      .then((rows) => { if (!cancelled) setRoles(rows); })
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const selected = roles.find((r) => r.id === value);
  const fallback = roles.find((r) => r.isDefault);
  const visibleRoles = roles.filter(
    (r) => !hiddenSystemKeys.includes(r.systemKey ?? ''),
  );

  return (
    <div>
      <label htmlFor="user-role" className="block text-sm font-medium text-slate-700">
        Role
      </label>

      <select
        id="user-role"
        value={value ?? ''}
        disabled={disabled || loading || failed}
        onChange={(e) => onChange(e.target.value || undefined)}
        aria-invalid={error ? true : undefined}
        className={[
          'mt-1.5 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none transition focus:ring-2',
          'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
          error
            ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
            : 'border-slate-300 focus:border-brand-500 focus:ring-brand-100',
        ].join(' ')}
      >
        {/* The empty option is not "no role" - it means "use the default",
            which the API resolves. Label it so nobody reads it as blank. */}
        <option value="">
          {loading
            ? 'Loading roles...'
            : fallback
              ? `Default — ${fallback.name}`
              : 'Default'}
        </option>

        {visibleRoles.map((role) => (
          <option key={role.id} value={role.id}>
            {role.name}
            {role.memberCount > 0
              ? ` (${role.memberCount} ${role.memberCount === 1 ? 'person' : 'people'})`
              : ''}
          </option>
        ))}
      </select>

      {error ? (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      ) : failed ? (
        <p className="mt-1 text-xs text-amber-600">
          Could not load roles. The person will be created with the default role.
        </p>
      ) : selected?.description ? (
        <p className="mt-1 text-xs text-slate-500">{selected.description}</p>
      ) : (
        <p className="mt-1 text-xs text-slate-500">
          What this person is allowed to do. Leave as Default unless they need more.
        </p>
      )}

      {/* Super Admin can reset anyone's password and change organisation
          settings, so granting it deserves a beat of hesitation. */}
      {selected?.systemKey === 'SUPER_ADMIN' && (
        <p className="mt-1.5 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800 ring-1 ring-amber-200">
          Super Admins can manage everyone, reset passwords and change company
          settings.
        </p>
      )}
    </div>
  );
}
